import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { datadogRum } from "@datadog/browser-rum";
import { useMutation, useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { FC, ReactNode, SVGProps, useEffect, useState } from "react";
import { Link, NavLink, useMatch, useNavigate } from "react-router-dom";

import { get, post } from "api";
import { Icon } from "atoms/icon/Icon";
import AccountNav from "atoms/icon/icons/account_nav.svg";
import Cart from "atoms/icon/icons/cart.svg";
import ChevronRight from "atoms/icon/icons/chevron_right_small.svg";
import Search from "atoms/icon/icons/ic_search.svg";
import Warning from "atoms/icon/icons/ic_warning_ol.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import useDunningMembership from "hooks/useDunningMemberships";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisAiIcon from "modules/adonis/assets/ai-icon.svg";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { AdminImpersonationBanner } from "molecules/admin-impersonation-banner/AdminImpersonationBanner";
import { useFeatureFlag } from "services/Flags";
import Services from "services/Services";
import { buildPlatformUrl, setPromotionModalSeen } from "utils/functions";

type BaseProps = {
  children?: ReactNode;
  footer?: ReactNode;
  startIconSVG?: undefined;
  onStartIconClick?: undefined;
  hideShadow?: boolean;
  showHeaderItems?: boolean;
  /** Used for HomePage.tsx, because of the way we set TopNavV3 on every page.
   * To remove after setting TopNavV3 at a higher level(AppLayout, where Footer also is).
   */
  withAffirmOnTop?: boolean;
  withReferralRewardsOnTop?: boolean;
  backgroundVariant?: "transparent" | "default";
};

type WithIconProps = {
  startIconSVG: FC<SVGProps<SVGSVGElement>>;
  onStartIconClick: () => void;
} & Omit<BaseProps, "startIconSVG" | "onStartIconClick">;

type Props = BaseProps | WithIconProps;

const TopNavV3 = ({
  children,
  footer,
  startIconSVG,
  onStartIconClick,
  hideShadow = false,
  showHeaderItems = true,
  withAffirmOnTop,
  withReferralRewardsOnTop,
  backgroundVariant = "default",
}: Props) => {
  const navigate = useNavigate();
  const manageMembershipsPagePath = "/account/membership/manage";
  const manageMembershipsPageMatch = useMatch(manageMembershipsPagePath);
  const { dunningMembership } = useDunningMembership();
  const isAlgoliaFilteringEnabled = useFeatureFlag("algoliaFiltering");
  const isV3 = true;
  const {
    openChat,
    hasMessages: hasAdonisChatHistory,
    isEnabled: shouldShowAdonisCTA,
  } = useAdonisChat();

  const [isScrolled, setIsScrolled] = useState(false);
  const SCROLL_THRESHOLD = 5; // approximate height of banners

  const { data: cartDetails } = useQuery({
    queryKey: ["/orders/current/summary", { includePackageLineItems: false }],
    queryFn: () =>
      get("/orders/current/summary", { includePackageLineItems: false }),
  });

  const { data: authClaims } = useQuery({
    queryKey: ["/auth/claims"],
    queryFn: () => get("/auth/claims"),
  });

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const updatePaymentMutation = useMutation({
    mutationFn: (platUserMembId: string) =>
      post(
        "/platform-user-memberships/{platformUserMembershipId}/create-stripe-update-session",
        {
          platformUserMembershipId: platUserMembId,
          successUrl: url,
          cancelUrl: url,
        }
      ),
    onSuccess: (response) => {
      if (response && response.stripeSession.url) {
        window.location.href = response.stripeSession.url;
      } else {
        console.log("error ocurred");
      }
    },
  });

  useEffect(() => {
    (async () => {
      if (user?.isBlocked) {
        setPromotionModalSeen(false);
        await Services.auth.clearAll();
        datadogRum.clearUser();
        navigate("/signin");
      }
    })();
  }, [user?.isBlocked]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > SCROLL_THRESHOLD);
    };

    window.addEventListener("scroll", handleScroll);

    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [SCROLL_THRESHOLD]);

  const isNativePlatform = Capacitor.isNativePlatform();
  const isAdminImpersonation = !!authClaims?.claims.adminUserId;
  const url = buildPlatformUrl(
    platform?.scheme,
    platform?.subdomain,
    platform?.host
  );
  const cartCount = cartDetails?.numShopDisplayOrderLineItems || 0;

  const handleUpdatePayment = async (membershipId: string) => {
    if (isNativePlatform) {
      handleUpdatePaymentOnCapacitor(membershipId);
    } else {
      updatePaymentMutation.mutate(membershipId);
    }
  };

  const handleUpdatePaymentOnCapacitor = async (membershipId: string) => {
    const response = await post(
      "/platform-user-memberships/{platformUserMembershipId}/create-stripe-update-session",
      {
        platformUserMembershipId: membershipId,
        successUrl: `${url}/stripe-landing/close-browser?successfulPayment=true`,
        cancelUrl: `${url}/stripe-landing/close-browser?successfulPayment=false`,
      }
    );

    Browser.addListener("browserFinished", async () => {
      Browser.removeAllListeners();
      window.scroll(0, 0);
    });

    await Browser.open({ url: response.stripeSession.url });
  };

  const displayCart =
    !user?.isGuest &&
    (platform?.platformSettings?.marketplaceEnabled || cartCount > 0);

  const accountLink = user?.isGuest ? "/signin" : "/account";

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const renderDunningMembershipBanner = () => {
    return (
      dunningMembership.status &&
      !isV3 && (
        <div className="sticky top-0 z-10 flex items-center justify-between bg-red py-3 pl-4 pr-3 text-bold2 text-white">
          <div className="flex gap-2">
            <Icon svg={Warning} size="x2small" color="white" />
            Membership payment failed
          </div>
          {!manageMembershipsPageMatch && (
            <Link to={manageMembershipsPagePath} className="flex">
              Update
              <Icon svg={ChevronRight} size="x2small" color="white" />
            </Link>
          )}
        </div>
      )
    );
  };

  const renderMembershipFailedBanner = () => {
    return (
      dunningMembership.status &&
      isV3 && (
        <div className="sticky top-0 z-10 flex items-center justify-between bg-[#8B0000] py-1.5 pl-4 pr-3 text-bold2 text-white">
          <div className="flex gap-2">
            <Icon svg={Warning} size="medium" color="white" />
            Your recent membership payment failed.
          </div>
          {!manageMembershipsPageMatch && (
            <Link
              to={manageMembershipsPagePath}
              className="flex items-center gap-1 rounded-full border-2 border-white bg-white px-4 py-1 text-bold2 text-[#8B0000]"
              style={{ animation: "urgentPulse 1.5s ease-in-out infinite" }}
            >
              Fix
            </Link>
          )}
        </div>
      )
    );
  };

  const renderImpersonationBanner = () => {
    return (
      isAdminImpersonation &&
      !!user && (
        <AdminImpersonationBanner
          platformUserDisplayName={
            user.email || (user.firstName && user.lastName)
              ? user.email || `${user.firstName} ${user.lastName}`
              : user.phoneNumber
          }
        />
      )
    );
  };

  const renderAdonisCTA = () => {
    return (
      shouldShowAdonisCTA && (
        <div className="relative px-1.5">
          <button
            onClick={() => {
              trackAdonisViewed();
              openChat();
            }}
          >
            <Icon svg={AdonisAiIcon} size="small" />
            {hasAdonisChatHistory && (
              <div className="absolute -right-[2px] -top-[2px] inline-flex h-2.5 w-2.5 rounded-full border-2 border-white bg-red text-xs font-bold text-white" />
            )}
          </button>
        </div>
      )
    );
  };

  const renderCart = () => {
    return (
      displayCart && (
        <div className="flex gap-3 px-1.5">
          <NavLink to="/cart">
            <div className="relative">
              <Icon svg={Cart} />
              {cartDetails &&
                cartDetails.numShopDisplayOrderLineItems != null &&
                cartDetails.numShopDisplayOrderLineItems > 0 && (
                  <div className="absolute -right-2 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-brand-color text-xs font-bold text-white">
                    {cartDetails.numShopDisplayOrderLineItems}
                  </div>
                )}
            </div>
          </NavLink>
        </div>
      )
    );
  };

  return (
    <div
      className={cx(
        "sticky top-0 z-20 transition-colors duration-200",
        isScrolled || backgroundVariant === "default"
          ? "bg-white"
          : "bg-transparent",
        isScrolled && "shadow-[0px_2px_4px_rgba(0,0,0,0.05)]",
        withAffirmOnTop || withReferralRewardsOnTop
          ? "top-[env(safe-area-inset-top)]"
          : "pt-[env(safe-area-inset-top)]"
      )}
    >
      {renderImpersonationBanner()}

      {renderMembershipFailedBanner()}

      {renderDunningMembershipBanner()}

      <div
        className={cx(
          "flex h-[54px] items-center justify-between pb-[5px] pt-[5px] font-bold",
          startIconSVG ? "pr-5" : "px-5"
        )}
      >
        {startIconSVG && (
          <IconButton
            svg={startIconSVG}
            size="small"
            style="iconOnly"
            onClick={onStartIconClick}
          />
        )}

        <div className="flex flex-1 items-center pl-1 [&:not(:first-child)]:justify-start">
          {children}
        </div>

        {startIconSVG && <div className="invisible w-6"></div>}

        {showHeaderItems && (
          <>
            {platform?.platformSettings?.marketplaceEnabled && (
              <div className="px-1.5">
                <NavLink
                  to={
                    isAlgoliaFilteringEnabled ? "/search-services" : "/search"
                  }
                >
                  <Icon svg={Search} />
                </NavLink>
              </div>
            )}

            {renderAdonisCTA()}

            <div className="px-1.5">
              <NavLink to={accountLink}>
                <Icon svg={AccountNav} />
              </NavLink>
            </div>

            {renderCart()}
          </>
        )}
      </div>

      <div>{footer}</div>
    </div>
  );
};

export default TopNavV3;
